<template>
  <div>
    <div class="flex-between">
      <div>
        <h3>{{ label }}</h3>
        <p>{{ description }}</p>
      </div>
      <div class="export-controls">
        <v-select
          class="export-format-select"
          v-model="selectedFormat"
          :items="['XLSX', 'CSV']"
          dense
          flat
          solo-inverted
          hide-details
        />
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              v-bind="attrs"
              v-on="on"
              @click="exportPayoutReport"
              :disabled="isGeneratingReport || items.length === 0"
            >
              <v-icon v-if="!isGeneratingReport">mdi-exit-to-app</v-icon>
              <v-progress-circular indeterminate color="primary" size="20" width="2" v-else />
            </v-btn>
          </template>
          <span>Export Report</span>
        </v-tooltip>
      </div>
    </div>
    <v-data-table
      v-bind="$attrs"
      v-on="$listeners"
      :items="items"
      :headers="computedHeaders"
      :server-items-length="total"
      :options.sync="options"
      :loading="loading"
      :item-class="getRowClass"
      :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
    >
      <template v-slot:item.createdDate="{ item }">
        <div class="d-flex align-center text-no-wrap">
          <span>{{ getDisplayDate(item) | formatDateClient("MM/DD/YYYY", selectedClient) }}</span>
          <v-tooltip bottom v-if="isStandaloneAdjustment(item)">
            <template v-slot:activator="{ on, attrs }">
              <v-icon small color="primary" class="ml-2" v-bind="attrs" v-on="on">mdi-information</v-icon>
            </template>
            <span>
              <strong>Standalone Adjustment</strong>
              <template v-if="item.payoutMessage">
                <br />
                {{ item.payoutMessage }}
              </template>
            </span>
          </v-tooltip>
        </div>
      </template>
      <template v-slot:item.dealer="{ item }">
        <span v-if="item.sale && item.sale.organization">
          {{
            item.sale.organization.legacyOrganizationKey
              ? item.sale.organization.legacyOrganizationKey
              : item.sale.organization.organizationKey
          }}
          -
          {{ item.sale.organization.name }}
        </span>
        <span v-else-if="isStandaloneAdjustment(item) && item.organization">
          {{
            item.organization.legacyOrganizationKey
              ? item.organization.legacyOrganizationKey
              : item.organization.organizationKey
          }}
          -
          {{ item.organization.name }}
        </span>
        <span v-else-if="isStandaloneAdjustment(item)" class="unavailable-value">—</span>
      </template>
      <template v-slot:item.program="{ item }">
        <span v-if="item.promotion && item.promotion.promotionType">
          {{ item.promotion.promotionType.promotionTypeKey }} -
          {{ item.promotion.promotionType.name }}
        </span>
        <span v-else-if="isStandaloneAdjustment(item)" class="unavailable-value">—</span>
      </template>
      <template v-slot:item.model="{ item }">
        <span v-if="item.saleProduct && item.saleProduct.product">
          {{ item.saleProduct.product.productKey }}
        </span>
        <span v-else-if="isStandaloneAdjustment(item)">
          <span v-if="item.resolvedProduct">{{ item.resolvedProduct.productKey }}</span>
          <span v-else class="unavailable-value">—</span>
        </span>
        <span v-else-if="isStandaloneAdjustment(item)" class="unavailable-value">—</span>
      </template>
      <template v-slot:item.vin="{ item }">
        <span v-if="isStandaloneAdjustment(item)">{{ item.customFieldValue8 }}</span>
        <span v-else>{{ item?.saleProduct?.serialNumber }}</span>
      </template>
      <template v-slot:item.reason="{ item }">
        <span>
          {{ item.cancelledReason }}
        </span>
      </template>
      <template v-slot:item.retailPurchaseDate="{ item }">
        <span v-if="item.sale && item.sale.customFieldValue9">
          {{ item.sale.customFieldValue9 | formatDateClient("MM/DD/YYYY", selectedClient) }}
        </span>
        <span v-else-if="isStandaloneAdjustment(item)" class="unavailable-value">—</span>
      </template>

      <template v-slot:item.payoutAmount="{ item }">
        {{ item.calculatedPayoutAmount | toNumber(2, selectedProgram) }}
        {{ item.calculatedPayoutAmountCurrency ? item.calculatedPayoutAmountCurrency : "" }}
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "SalespersonEarningsTable",
  props: {
    filters: { type: Object, default: () => ({}) },
    search: { type: String, default: "" },
    pending: { type: Boolean, default: null },
    expired: { type: Boolean, default: null },
    label: String,
    description: String
  },
  components: {},
  data() {
    return {
      tab: null,
      loading: true,
      items: [],
      nameFilter: "",
      expanded: [],
      isGeneratingReport: false,
      selectedFormat: "XLSX",
      customReportsByKey: {},
      headers: [
        {
          value: "createdDate",
          text: "Earned Date",
          sortable: true
        },
        {
          value: "dealer",
          text: "Dealer",
          sortable: false
        },
        {
          value: "program",
          text: "Program",
          sortable: false
        },
        {
          value: "model",
          text: "Model",
          sortable: false
        },
        {
          value: "vin",
          text: "VIN",
          sortable: false
        },
        {
          value: "reason",
          text: "Reason",
          sortable: false
        },
        {
          value: "retailPurchaseDate",
          text: "Retail Purchase Date",
          sortable: false
        },
        {
          value: "awardVehicle.name",
          text: "Award Type",
          sortable: true
        },
        {
          value: "payoutAmount",
          text: "Value",
          sortable: true,
          align: "right"
        }
      ],
      total: 0,
      options: {
        sortBy: ["createdDate"],
        sortDesc: [true],
        itemsPerPage: 10
      }
    };
  },
  methods: {
    onClear() {
      this.search = "";
      this.getData();
    },
    onSubmit() {
      this.options.page = 0;
      this.getData();
    },
    getCreatedAfterValue() {
      return this.filters?.createdAfter || this.selectedFiscalYear?.fiscalYearStart || null;
    },
    getCreatedBeforeValue() {
      return this.filters?.createdBefore || this.selectedFiscalYear?.fiscalYearEnd || null;
    },
    isStandaloneAdjustment(item) {
      return !item.sale && !item.saleProduct;
    },
    getRowClass(item) {
      return this.isStandaloneAdjustment(item) ? "adjustment-row primary lighten-5" : "";
    },
    getDisplayDate(item) {
      if (this.expired) {
        return item.expirationDate;
      }
      if (this.pending) {
        return item.createdDate;
      }
      return item.bankTransaction?.referenceBankTransaction?.issuedDate || null;
    },
    getData() {
      this.loading = true;

      let filters = {
        participant: { id: this.selectedParticipant.id }
      };

      if (this.expired) {
        filters.payoutStatus = "expired";
      } else {
        filters.isEffective = true;
        filters.payoutStatus = this.pending ? "pending" : "issued";
      }

      if (this.search != null && this.search.trim().length > 0) {
        filters.keyword = this.search.trim();
      }

      const createdAfter = this.getCreatedAfterValue();
      const createdBefore = this.getCreatedBeforeValue();
      if (createdAfter && createdBefore) {
        if (!this.expired && !this.pending) {
          filters.issuedAfter = createdAfter;
          filters.issuedBefore = createdBefore;
        } else {
          filters.createdAfter = createdAfter;
          filters.createdBefore = createdBefore;
        }
      }

      const params = this.$util.buildPaginationParams(this.options);
      params.append("expand", "sale.organization");
      params.append("expand", "saleProduct.product");
      params.append("expand", "organization");
      params.append("expand", "awardVehicle");
      params.append("expand", "promotion.promotionType");

      return this.$api
        .post(`/api/payouts/search?${params.toString()}`, filters)
        .then(({ data }) => {
          this.items = data.content;
          this.total = data.totalElements;
          this.items.forEach(item => {
            if (this.isStandaloneAdjustment(item) && item.customFieldValue8) {
              this.$api
                .get("/api/products/search/findBySerialNumber?serialNumber=" + item.customFieldValue8)
                .then(({ data }) => {
                  if (data?._embedded?.products && data?._embedded?.products.length > 0) {
                    this.$set(item, "resolvedProduct", data._embedded.products[0]);
                  }
                });
            }
          });
        })
        .catch(e => {
          console.log(e);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    exportPayoutReport() {
      let reportKey = "EXPORT_SALESPERSON_PAYOUT_EARNED";
      if (this.pending) {
        reportKey = "EXPORT_SALESPERSON_PAYOUT_PENDING";
      } else if (this.expired) {
        reportKey = "EXPORT_SALESPERSON_PAYOUT_EXPIRED";
      }

      return this.generateCustomReport(reportKey);
    },
    getCustomReport(reportKey) {
      if (this.customReportsByKey[reportKey]) {
        return Promise.resolve(this.customReportsByKey[reportKey]);
      }

      return this.$api
        .post("/api/customReports/search?size=1&page=0", {
          customReportKey: reportKey
        })
        .then(({ data }) => {
          const customReport = data?.content?.[0] || null;
          this.customReportsByKey = {
            ...this.customReportsByKey,
            [reportKey]: customReport
          };
          return customReport;
        });
    },
    buildCustomReportFilters(customReport) {
      const createdAfter = this.getCreatedAfterValue();
      const createdBefore = this.getCreatedBeforeValue();
      const createdAfterFilter = customReport.customReportFilters.find(
        filter => filter.name === "payout_after_created_date"
      );
      const createdBeforeFilter = customReport.customReportFilters.find(
        filter => filter.name === "payout_before_created_date"
      );

      const customReportFilters = [];

      if (createdAfterFilter && createdAfter) {
        customReportFilters.push({
          id: createdAfterFilter.id,
          value: createdAfter
        });
      }

      if (createdBeforeFilter && createdBefore) {
        customReportFilters.push({
          id: createdBeforeFilter.id,
          value: createdBefore
        });
      }

      return customReportFilters;
    },
    generateCustomReport(reportKey) {
      this.isGeneratingReport = true;

      return this.getCustomReport(reportKey)
        .then(customReport => {
          return this.$api.post(`/api/customReports/generateByKey/${reportKey}/${this.selectedFormat}`, {
            customReportFilters: this.buildCustomReportFilters(customReport)
          });
        })
        .then(() => {
          this.$router.push({ name: "reportDownloads" });
        })
        .catch(error => {
          console.log(error);
        })
        .finally(() => {
          this.isGeneratingReport = false;
        });
    }
  },
  mounted() {
    this.$store.dispatch("setBreadcrumb", [
      {
        text: this.$i18n.translate("Dashboard"),
        to: { name: "dashboard" },
        exact: true
      },
      { text: "Reports", exact: true },
      { text: "My Earnings" }
    ]);
  },
  watch: {
    options: {
      handler() {
        this.getData();
      },
      deep: true
    },
    search() {
      this.getData();
    },
    selectedFiscalYear() {
      this.getData();
    }
  },
  computed: {
    ...mapGetters(["selectedParticipant", "selectedClient", "selectedProgram", "selectedFiscalYear"]),
    computedHeaders() {
      const headers = this.headers.map(header =>
        header.value === "createdDate" && this.expired ? { ...header, text: "Expiration Date" } : header
      );
      return this.pending == true || this.expired == true
        ? headers
        : headers.filter(header => header.value !== "reason");
    }
  }
};
</script>
<style>
.flex-between {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}

.export-controls {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.export-format-select {
  width: 120px;
}

.unavailable-value {
  color: rgba(0, 0, 0, 0.54);
}

.v-data-table tbody tr.adjustment-row > td {
  background-color: inherit;
}
</style>
